import * as React from 'react';

type SidebarToggleIconProps = {
  /** Stroke width in px (defaults to 24 like your original) */
  strokeWidth?: number;
  /** Stroke/fill color */
  color?: string;
  /** Outer corner radius */
  radius?: number;
  /** Direction of chevron in the right panel */
  direction: 'left' | 'right';
  /** Optional size override (defaults to 512) */
  size?: number;
  /**
   * If true, keeps stroke thickness constant when the SVG is scaled via CSS transforms.
   * Usually leave false; enable only if the icon is being scaled unexpectedly.
   */
  nonScalingStroke?: boolean;
};

export function SidebarToggleIcon({
  strokeWidth = 24,
  color = '#009c7e',
  radius = 4,
  direction,
  size = 512,
  nonScalingStroke = false,
}: SidebarToggleIconProps) {
  // Layout constants (viewBox units)
  const vb = 512;

  // Snap helper: prevents fractional coordinates that blur at small sizes.
  const snap = (n: number) => Math.round(n);

  // Outer container is now a slight rectangle (wider than tall).
  // Left panel exists only as the divider (no square/menu items).
  const outer = {
    x: 64,
    y: 96,
    w: 384, // wider than before
    h: 320,
  };

  // Divider creates the (empty) left panel and the right panel.
  const dividerX = outer.x + 120; // left panel width = 120

  // Right panel bounds and center
  const rightPane = {
    x0: dividerX,
    x1: outer.x + outer.w,
    y0: outer.y,
    y1: outer.y + outer.h,
  };

  const cx = (rightPane.x0 + rightPane.x1) / 2;
  const cy = (rightPane.y0 + rightPane.y1) / 2;

  // chevron sizing (scales with stroke width so it stays legible)
  const chevronHalfHeight = 42;
  const chevronHalfWidth = 30;

  const leftPoints = [
    [cx + chevronHalfWidth, cy - chevronHalfHeight],
    [cx - chevronHalfWidth, cy],
    [cx + chevronHalfWidth, cy + chevronHalfHeight],
  ]
    .map(([x, y]) => `${snap(x)},${snap(y)}`)
    .join(' ');

  const rightPoints = [
    [cx - chevronHalfWidth, cy - chevronHalfHeight],
    [cx + chevronHalfWidth, cy],
    [cx - chevronHalfWidth, cy + chevronHalfHeight],
  ]
    .map(([x, y]) => `${snap(x)},${snap(y)}`)
    .join(' ');

  const ve = nonScalingStroke ? ('non-scaling-stroke' as const) : undefined;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={`0 0 ${vb} ${vb}`}
      fill="none"
      role="img"
      aria-label={`Sidebar toggle ${direction}`}
      shapeRendering="geometricPrecision"
      style={{ display: 'block', flexShrink: 0 }}
    >
      {/* Outer container */}
      <rect
        x={snap(outer.x)}
        y={snap(outer.y)}
        width={snap(outer.w)}
        height={snap(outer.h)}
        rx={snap(radius)}
        ry={snap(radius)}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="miter"
        shapeRendering="crispEdges"
        vectorEffect={ve}
      />

      {/* Divider */}
      <line
        x1={snap(dividerX)}
        y1={snap(outer.y + strokeWidth / 2)}
        x2={snap(dividerX)}
        y2={snap(outer.y + outer.h - strokeWidth / 2)}
        stroke={color}
        strokeWidth={strokeWidth + 8} // enhance divider width
        strokeLinecap="square"
        shapeRendering="crispEdges"
        vectorEffect={ve}
      />

      {/* Chevron */}
      <polyline
        points={direction === 'left' ? leftPoints : rightPoints}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect={ve}
      />
    </svg>
  );
}
